/**
 * Breadcrumb config for the non-portfolio/project/timesheet management contexts
 * (organizational, personnel, contract, supply-chain, configuration, my-work,
 * workflow-management). Portfolio/project + timesheet keep their bespoke trails
 * in Breadcrumb.tsx; this covers "everything else" so EVERY page in Tesseract
 * gets a `Context › Page` trail.
 *
 * Route→label pairs mirror the sidebar nav (Sidebar.tsx). A longest-prefix match
 * gives the page label; anything unmapped (dynamic/detail routes) falls back to a
 * title-cased path segment, so no page is ever crumb-less.
 */
import type { ManagementContext } from '@/store/appStore'

/** The root ("you are here" home) per context: label + landing path. */
export const CONTEXT_ROOTS: Partial<Record<ManagementContext, { label: string; path: string }>> = {
  organizational: { label: 'Organization', path: '/organizations' },
  personnel: { label: 'Organization', path: '/organizations' }, // Personnel sits under the org umbrella
  contract: { label: 'Contracts', path: '/contracts' },
  supplychain: { label: 'Supply Chain', path: '/supply-chain' },
  configuration: { label: 'Configuration', path: '/config' },
  mywork: { label: 'My Work', path: '/my-work' },
  'workflow-management': { label: 'Workflow Management', path: '/workflow-management/overview' },
  analytics: { label: 'Analytics', path: '/analytics' },
  cost: { label: 'Cost Management', path: '/cost/overview' },
}

/** path → page label. Mirrors the sidebar nav item labels (kept in sync by hand,
 *  same as PROJECT_PAGE_NAMES/PORTFOLIO_PAGE_NAMES in Breadcrumb.tsx). */
export const CONTEXT_PAGE_NAMES: Record<string, string> = {
  // ── Contracts (CLM) ──
  '/contracts': 'Contract Dashboard',
  '/contracts/all': 'All Contracts',
  '/contracts/approvals': 'My Approvals',
  '/contracts/templates': 'Clause Templates',
  '/contracts/preferred-terms': 'Preferred Terms',
  '/contracts/customers': 'Customer Analysis',
  '/contracts/analytics': 'Workflow Analytics',
  // ── Personnel / Organization ──
  '/personnel': 'Personnel Directory',
  '/organizations': 'Org Hierarchy',
  '/organization/rates': 'Rates Management',
  // ── Analytics Studio ──
  '/analytics': 'Conversational Analytics',
  '/analytics/knowledge-graph': 'Knowledge Graph',
  // ── Workflow Management ──
  '/workflow-management/overview': 'Overview',
  '/workflow-management/studio': 'Workflows',
  '/workflow-management/forms': 'Forms',
  '/workflow-management/requests': 'Submit a Request',
  '/workflow-management/my-submissions': 'My Requests',
  '/workflow-management/inbox': 'Approvals',
  '/workflow-management/delegations': 'Delegations',
  '/workflow-management/runs': 'Runs',
  '/workflow-management/templates': 'Templates',
  '/workflow-management/libraries': 'Document Libraries',
  '/workflow-management/groups': 'People Groups',
  '/workflow-management/ai-connection': 'AI Connection',
  '/workflow-management/directory': 'Directory (Entra ID)',
  '/workflow-management/email': 'Email',
  '/workflow-management/sites': 'Sites',
  '/workflow-management/routing-matrix': 'Routing Matrix',
  '/workflow-management/access': 'Access and CUI',
  '/workflow-management/teams': 'Teams',
  // ── Supply Chain ──
  '/supply-chain': 'Planning Home',
  '/supply-chain/demand': 'Demand Workbench',
  '/supply-chain/supply': 'Supply & MRP',
  '/supply-chain/inventory': 'Inventory & DDMRP',
  '/supply-chain/capacity': 'Capacity',
  '/supply-chain/gpd': 'GPD & Pegging',
  '/supply-chain/cost-pegging': 'Actual Cost Pegging',
  '/supply-chain/genealogy': 'Part Genealogy',
  '/supply-chain/pegging-exceptions': 'Pegging Exceptions',
  '/supply-chain/promising': 'Order Promising',
  '/supply-chain/response': 'Scenarios & Response',
  '/supply-chain/control-tower': 'Control Tower',
  '/supply-chain/supplier': 'Supplier & SCFM',
  '/supply-chain/compliance': 'Compliance',
  '/supply-chain/sustainment': 'Sustainment & MRO',
  '/supply-chain/sop': 'S&OP / IBP',
  // ── Cost Management ──
  '/cost/overview': 'Cost Overview',
  '/cost/ledger': 'Universal Ledger',
  '/cost/rates': 'Pools & Rate Segments',
  '/cost/allocations': 'Direct Allocations',
  '/cost/burdens': 'Burden Rates',
  '/cost/transfers': 'Cost Transfers',
  '/cost/icr': 'Incurred Cost Reporting',
  '/cost/pegged-accrual': 'Pegged Cost Accrual',
  '/cost/postings': 'Posting Log',
  // ── My Work ──
  '/my-work': 'Home',
  '/my-work/inbox': 'Action Inbox',
  '/my-work/tasks': 'My Tasks',
  '/my-work/risks': 'My Risks',
  '/my-work/calendar': 'Calendar',
  '/my-profile': 'My Profile',
  '/skill-approvals': 'Skill Approvals',
  // ── Configuration ──
  '/config': 'Dashboard',
  '/config/users': 'User Management',
  '/config/org-directory': 'Org Directory',
  '/config/roles': 'Role Assignments',
  '/config/policies': 'ABAC Policies',
  '/config/ui-mappings': 'UI Policy Mappings',
  '/config/project-roles': 'Project Roles',
  '/config/disciplines': 'Disciplines',
  '/config/expertise': 'Expertise Catalog',
  '/config/certifications': 'Certifications',
  '/config/tools': 'Tools / Technologies',
  '/config/chart-of-accounts': 'Chart of Accounts',
  '/config/cost-planning-accounts': 'Cost Planning Accounts',
  '/config/budget-categories': 'Budget Categories',
  '/config/wbs-attribute-library': 'WBS Attribute Library',
  '/config/resource-planning': 'Resource Planning',
  '/config/resource-fields': 'Resource Fields',
  '/config/project-lifecycle': 'Project Lifecycle',
  '/config/locations': 'Locations',
  '/config/sops': 'Standard Operating Procedures',
  '/config/manufacturing-time': 'Manufacturing Time',
  '/config/time-evaluation-rules': 'Time Evaluation Rules',
  '/config/instance-setup': 'Instance Setup',
  '/config/client-setup': 'Client Setup',
  '/config/demo-library': 'Demo Library',
  '/config/feature-flags': 'Feature Flags',
  '/config/audit': 'Audit Log',
}

/**
 * A group crumb between the context root and the page, for pages the sidebar
 * shows inside a labelled group (Sidebar.tsx). Path prefix to group label; the
 * group has no page of its own, so the crumb is not a link.
 */
export const CONTEXT_PAGE_PARENTS: Record<string, string> = {
  '/supply-chain/cost-pegging': 'Actual Cost Pegging Engine',
  '/supply-chain/genealogy': 'Actual Cost Pegging Engine',
  '/supply-chain/pegging-exceptions': 'Actual Cost Pegging Engine',
}

/** The group crumb for a path (exact or a detail route under it), or null. */
export function resolveGenericPageParent(path: string): string | null {
  if (!path) return null
  for (const [prefix, label] of Object.entries(CONTEXT_PAGE_PARENTS)) {
    if (path === prefix || path.startsWith(prefix + '/')) return label
  }
  return null
}

/** Title-case a raw path segment as a last-resort label (dynamic/detail routes),
 *  so no page is ever crumb-less. */
export function titleCaseSegment(seg: string): string {
  return seg
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Resolve a page label for a path: longest-prefix match in CONTEXT_PAGE_NAMES,
 * else title-case the last non-empty path segment. Returns null only for the bare
 * root "/".
 */
const ROOT_PATHS = new Set(Object.values(CONTEXT_ROOTS).map((r) => r!.path))

export function resolveGenericPageLabel(path: string): string | null {
  if (!path || path === '/') return null
  // Exact match first (covers context roots + every exact page).
  const exact = CONTEXT_PAGE_NAMES[path]
  if (exact) return exact
  // Longest-prefix match, EXCLUDING context roots — otherwise a short root like
  // `/config` would swallow an unmapped `/config/xyz` (reading "Dashboard")
  // instead of title-casing the specific segment. Real page prefixes (e.g.
  // `/supply-chain/demand`) still resolve their detail routes.
  let best = ''
  let label: string | undefined
  for (const [prefix, name] of Object.entries(CONTEXT_PAGE_NAMES)) {
    if (ROOT_PATHS.has(prefix)) continue
    if (path.startsWith(prefix + '/') && prefix.length > best.length) {
      best = prefix
      label = name
    }
  }
  if (label) return label
  const segs = path.split('/').filter(Boolean)
  const last = segs[segs.length - 1]
  return last ? titleCaseSegment(last) : null
}
